type FbqFn = (...args: unknown[]) => void;

// ✅ Read cookies for fbc and fbp
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

// ✅ Base params included with every event
function getBaseParams(): Record<string, unknown> {
  const fbc = getCookie("_fbc");
  const fbp = getCookie("_fbp");
  const params: Record<string, unknown> = {};
  if (fbc) params.fbc = fbc;
  if (fbp) params.fbp = fbp;
  return params;
}

function fbq(
  eventType: "track" | "trackCustom",
  eventName: string,
  params?: Record<string, unknown>,
  options?: Record<string, unknown>
): void {
  if (typeof window === "undefined") return;
  const _fbq = (window as unknown as { fbq?: FbqFn }).fbq;
  if (!_fbq) return;
  _fbq(eventType, eventName, { ...getBaseParams(), ...(params ?? {}) }, options);
}

/**
 * Track when a user submits lead info (sign-up form filled).
 * Fire on: email entered in sign-up, email popup submitted.
 *
 * Note: no raw email is sent. The browser pixel does not auto-hash values in a
 * track call's data, so passing a plaintext `em` here would leak PII to Meta in
 * cleartext (GDPR/CCPA) and isn't valid Advanced Matching anyway. If email match
 * quality is needed, hash it (SHA-256, lowercased/trimmed) or send it server-
 * side via the Conversions API.
 */
export function trackFbLead(): void {
  fbq("track", "Lead");
}

/**
 * Track completed registration (OTP verified on sign-up).
 * Fire on: sign-up OTP verified successfully.
 */
export function trackFbCompleteRegistration(params?: {
  status?: string;
}): void {
  fbq("track", "CompleteRegistration", {
    status: params?.status ?? "success",
  });
}

/**
 * ✅ Track login (OTP verified on login).
 * Fire on: login OTP verified successfully.
 */
export function trackFbLogin(params?: { userId?: string }): void {
  fbq("trackCustom", "Login", {
    ...(params?.userId && { external_id: params.userId }),
  });
}

/**
 * ✅ Track CTA button clicks.
 * Fire on: any important button click.
 */
export function trackFbCTAClick(params?: {
  buttonName?: string;
  location?: string;
}): void {
  fbq("trackCustom", "CTAClick", {
    ...(params?.buttonName && { button_name: params.buttonName }),
    ...(params?.location && { location: params.location }),
  });
}

/**
 * Track when a pricing plan or addon checkout is initiated.
 * Fire on: pricing plan "Buy Now" click, settings addon "Buy Add-on" click.
 */
export function trackFbInitiateCheckout(params: {
  contentIds: string[];
  value?: number;
  currency?: string;
  numItems?: number;
}): void {
  const payload: Record<string, unknown> = {
    content_ids: params.contentIds,
    content_type: "product",
  };
  if (params.value !== undefined && Number.isFinite(params.value)) {
    payload.value = params.value;
  }
  if (params.currency) payload.currency = params.currency;
  if (params.numItems !== undefined) payload.num_items = params.numItems;
  fbq("track", "InitiateCheckout", payload);
}

/**
 * Track a completed purchase (subscription activated or addon bought).
 * Fire on: dashboard checkout success verification.
 */
export function trackFbPurchase(params: {
  value: number;
  currency: string;
  contentIds?: string[];
  numItems?: number;
  eventId?: string;
}): void {
  const payload: Record<string, unknown> = {
    value: params.value,
    currency: params.currency,
    content_type: "product",
  };
  if (params.contentIds?.length) payload.content_ids = params.contentIds;
  if (params.numItems !== undefined) payload.num_items = params.numItems;
  fbq("track", "Purchase", payload, params.eventId ? { eventID: params.eventId } : undefined);
}

/**
 * Track when a user performs a search (hero form submitted).
 * Fire on: "Find Leads" button click with interest/location.
 */
export function trackFbSearch(params?: {
  searchString?: string;
  contentIds?: string[];
}): void {
  const payload: Record<string, unknown> = {};
  if (params?.searchString) payload.search_string = params.searchString;
  if (params?.contentIds?.length) payload.content_ids = params.contentIds;
  fbq("track", "Search", payload);
}

/**
 * Track when a user views key content (pricing section).
 * Fire on: pricing section scrolled into view.
 */
export function trackFbViewContent(params?: {
  contentName?: string;
  contentIds?: string[];
  value?: number;
  currency?: string;
}): void {
  const payload: Record<string, unknown> = {
    content_type: "product",
  };
  if (params?.contentName) payload.content_name = params.contentName;
  if (params?.contentIds?.length) payload.content_ids = params.contentIds;
  if (params?.value !== undefined && Number.isFinite(params.value)) payload.value = params.value;
  if (params?.currency) payload.currency = params.currency;
  fbq("track", "ViewContent", payload);
}
